import { useCallback, useEffect, useRef } from "react";
import { Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth0 } from "@auth0/auth0-react";

// Components
import Navbar from "../../UI/Navbar";
import Footer from "../../UI/Footer";
import Box from "@mui/material/Box";

// Utilities
import LayoutHeightContext from "../../../contexts/LayoutHeight";
import useClinic from "../../../hooks/useClinic";
import { getClinics } from "../../../services/Clinic";
import { allLanguageMap, languageMap } from "../../../i18n/languages";
import { useComponentDimensionsById } from "../../../hooks/useComponentDimensionsById";

const Layout = () => {
  const { getAccessTokenSilently } = useAuth0();
  const {
    clinicId,
    clinicLang,
    setClinicLang,
    setClinicLicensing,
    setPatientDefaultLang,
    setAllClinicInfo,
  } = useClinic();
  const { i18n } = useTranslation();
  const navbarRef = useRef(null);
  const footerRef = useRef(null);

  const { dynamicHeight: navbarHeight } = useComponentDimensionsById(
    null,
    "#navbar-container"
  );
  const { dynamicHeight: footerHeight } = useComponentDimensionsById(
    null,
    "#footer-container"
  );

  const changeClinicLanguage = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const { settings, licensing } = await getClinics(token, clinicId);
      const getObjectKey = (obj, value) => {
        return Object.keys(obj).find((key) => obj[key].val === value);
      };
      if (settings?.clinicDefaultLanguage) {
        const langKey = getObjectKey(
          languageMap,
          settings.clinicDefaultLanguage
        );
        i18n.changeLanguage(langKey);
        setClinicLang(langKey);
      }
      if (settings?.patientDefaultLanguage)
        setPatientDefaultLang(
          getObjectKey(allLanguageMap, settings.patientDefaultLanguage)
        );
      if (licensing) setClinicLicensing(licensing);
    } catch (e) {}
  }, [
    clinicId,
    getAccessTokenSilently,
    i18n,
    setClinicLang,
    setClinicLicensing,
    setPatientDefaultLang,
  ]);
  useEffect(() => {
    if (clinicLang) i18n.changeLanguage(clinicLang);
  }, [clinicLang, i18n]);

  useEffect(() => {
    changeClinicLanguage();
  }, [changeClinicLanguage]);

  useEffect(() => {
    const fetchAllClinicInfo = async () => {
      try {
        const token = await getAccessTokenSilently();
        const clinicsList = await getClinics(token);
        setAllClinicInfo(clinicsList);
      } catch (e) {}
    };
    fetchAllClinicInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <LayoutHeightContext.Provider value={{ navbarHeight, footerHeight }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
        }}
      >
        <Navbar ref={navbarRef} />
        <Outlet />
        <Footer ref={footerRef} />
      </Box>
    </LayoutHeightContext.Provider>
  );
};

export default Layout;
